import { useState } from "react";
import { useEffect } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { deepCopyObject, getReportOnlyFields } from "../../utils/helpers";
import { helperGetIsoDate } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

const filterInviteTypes = [
  {
    value: "single",
    label: "Personal",
  },
  {
    value: "shared",
    label: "Shared",
  },
  {
    value: "autoCreated",
    label: "Auto Created",
  },
];

const filterUserTypes = [
  {
    value: "administrator",
    label: "Administrator",
  },
  {
    value: "partner",
    label: "Partner",
  },
  {
    value: "patient",
    label: "Patient",
  },
  {
    value: "provider",
    label: "Provider",
  },
];

const ReportDialog = props => {
  const {
    open,
    dialogTitle = "Create Report",
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "Remove",
    maxWidth = "sm",
    fullWidth = true,
    omniODM, //omni Data Model Object, a root level object representation, Pass in a COPY, the only way this works
    onDelete = null,
  } = props;

  const theme = useTheme();

  const [editedODM, setEditedODM] = useState(omniODM);

  const [readyOk, setReadyOk] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const onDeleteHandler = () => {
    onDelete(editedODM._id);
  };

  const onAcceptHandler = () => {
    editedODM.omniData["onlyFields"] = getReportOnlyFields(
      editedODM.omniData.reportType,
      editedODM.omniData.reportType === constants.reportTypeEnums.users && userType === "patient",
    );

    let theQuery = {};
    if (includeInviteStatus) {
      theQuery["omniData.invitationStatus"] = { "#regex": `${inviteStatus}`, "#options": "i" };
    }
    if (includeCampaign) {
      theQuery["omniData.target"] = { "#regex": `${campaign}`, "#options": "i" };
    }

    if (includeInviteType) {
      if (inviteType === filterInviteTypes[0].value) {
        //single
        theQuery["omniData.inviteType"] = {
          "#regex": `${filterInviteTypes[0].value}`,
          "#options": "i",
        };
      } else if (inviteType === filterInviteTypes[1].value) {
        //shared
        theQuery["omniData.inviteType"] = {
          "#regex": `${filterInviteTypes[1].value}`,
          "#options": "i",
        };
        if (includeCampaign) {
          theQuery["omniData.target"] = { "#regex": `${campaign}`, "#options": "i" };
        } else {
          theQuery["omniData.target"] = { "#regex": "^((?!default shar).)*$", "#options": "i" };
        }
      } else if (inviteType === filterInviteTypes[2].value) {
        //auto created
        theQuery["omniData.inviteType"] = {
          "#regex": `${filterInviteTypes[1].value}`,
          "#options": "i",
        };

        if (includeCampaign) {
          theQuery["omniData.target"] = { "#regex": `${campaign}`, "#options": "i" };
        } else {
          theQuery["omniData.target"] = { "#regex": "default shar", "#options": "i" };
        }
      }
    }

    if (editedODM.omniData.reportType === constants.reportTypeEnums.users) {
      editedODM.omniData["userType"] = userType;
      editedODM.omniData["includeInactive"] = includeInactive;
    }

    editedODM.omniData["query"] = theQuery;
    //other queries

    if (includeDateBefore && dateBeforeValid) {
      theQuery["omniAudit.createdDate"] = { "#lte": dateBefore };
      //keeping for ease of use
      editedODM.omniData["dateBefore"] = dateBefore;
    } else {
      delete editedODM.omniData.dateBefore;
    }

    if (includeDateAfter && dateAfterValid) {
      theQuery["omniAudit.createdDate"] = { "#gte": dateAfter };
      //keeping for ease of use
      editedODM.omniData["dateAfter"] = dateAfter;
    } else {
      delete editedODM.omniData.dateAfter;
    }

    props.onAccept(editedODM);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const [dateBefore, setDateBefore] = useState(helperGetIsoDate(90));
  const [dateBeforeValid, setDateBeforeValid] = useState(true);
  const [includeDateBefore, setIncludeDateBefore] = useState(false);

  useEffect(() => {
    let theDate = new Date(dateBefore);

    if (theDate instanceof Date && !isNaN(theDate)) {
      setDateBeforeValid(true);
    } else {
      setDateBeforeValid(false);
    }
  }, [dateBefore]);

  const [dateAfter, setDateAfter] = useState(helperGetIsoDate(30));
  const [dateAfterValid, setDateAfterValid] = useState(true);
  const [includeDateAfter, setIncludeDateAfter] = useState(false);

  useEffect(() => {
    let theDate = new Date(dateAfter);

    if (theDate instanceof Date && !isNaN(theDate)) {
      setDateAfterValid(true);
    } else {
      setDateAfterValid(false);
    }
  }, [dateAfter]);

  const [includeInviteStatus, setIncludeInviteStatus] = useState(false);
  const [inviteStatus, setInviteStatus] = useState(constants.inviteStatus[0].value);

  const [includeInviteType, setIncludeInviteType] = useState(false);
  const [inviteType, setInviteType] = useState(constants.inviteType.single);

  const [includeCampaign, setIncludeCampaign] = useState(false);
  const [campaign, setCampaign] = useState("");

  const [userType] = useState(filterUserTypes[2].value);

  const [includeInactive] = useState(false);

  useEffect(() => {
    editedODM.omniData.name.length > 0 ? setReadyOk(true) : setReadyOk(false);
  }, [editedODM]);

  useEffect(() => {
    try {
      //todo update this

      if (omniODM.omniData.hasOwnProperty("dateBefore")) {
        if (omniODM.omniData.dateBefore != null) {
          setIncludeDateBefore(true);
          setDateBefore(omniODM.omniData.dateBefore);
        }
      }

      if (omniODM.omniData.hasOwnProperty("dateAfter")) {
        if (omniODM.omniData.dateAfter != null) {
          setIncludeDateAfter(true);
          setDateAfter(omniODM.omniData.dateAfter);
        }
      }

      if (omniODM.omniData.hasOwnProperty("query") && omniODM.omniData.query) {
        if (omniODM.omniData.query.hasOwnProperty("omniData.invitationStatus")) {
          let theValue = omniODM.omniData.query["omniData.invitationStatus"]["$regex"];

          setIncludeInviteStatus(true);
          setInviteStatus(theValue);
        }

        if (omniODM.omniData.query.hasOwnProperty("omniData.target")) {
          //i dont' know if its case sensitive efault
          let theValue = omniODM.omniData.query["omniData.target"]["$regex"];

          if (!theValue.includes("efault")) {
            //it isn't the stupid type filter shit
            setIncludeCampaign(true);
            setCampaign(theValue);
          }
        }

        if (omniODM.omniData.query.hasOwnProperty("omniData.inviteType")) {
          setIncludeInviteType(true);
          let theValue = omniODM.omniData.query["omniData.inviteType"]["$regex"];

          setInviteType(theValue);
        }
      }
    } catch (exception) {
      console.log(exception);
    }
  }, [omniODM]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container>
          <Typography variant="overline" sx={{ marginBottom: `-${theme.spacing(0.5)}` }}>
            Generation Details
          </Typography>
          <Grid container item spacing={1}>
            <Grid item xs={12} sm={4}>
              {editedODM._id == null && (
                <OmniTextField
                  label="Type"
                  select
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"reportType"}
                  value={editedODM.omniData.reportType}
                  onChange={event => {
                    editedODM.omniData.reportType = event.target.value;
                    setEditedODM(deepCopyObject(editedODM));
                  }}
                  placeholder={"Report Type"}
                >
                  {constants.reportTypes.map(report => (
                    <OmniMenuItem key={report.value} value={report.value}>
                      {report.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              )}
              {omniODM._id != null && (
                <OmniTextField
                  label="Type"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"reportType"}
                  value={
                    constants.reportTypes.find(temp => temp.value === omniODM.omniData.reportType)
                      .length > 0
                      ? constants.reportTypes.find(
                          temp => temp.value === omniODM.omniData.reportType,
                        ).label
                      : ""
                  }
                  placeholder={"Report Type"}
                  disabled={true}
                ></OmniTextField>
              )}
            </Grid>
            <Grid item xs={12} sm={8}>
              <OmniTextField
                label="Title"
                type="textInput"
                name={uuid()} //prevents AutoComplete Suggestions
                variant="outlined"
                id={"name"}
                disabled={editedODM._id != null}
                value={editedODM.omniData.name}
                error={!editedODM.omniData.name}
                onChange={event => {
                  editedODM.omniData.name = event.target.value;
                  setEditedODM(deepCopyObject(editedODM));
                }}
                placeholder={"A Name for your Report"}
                required
              />
            </Grid>
          </Grid>
          <Divider
            sx={{
              borderRadius: 4,
              borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
              height: 1,
              width: "100%",
              marginBottom: theme.spacing(0.5),
              marginTop: theme.spacing(0.5),
            }}
          />
          {editedODM._id != null && (
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ marginTop: theme.spacing(1), marginBottom: theme.spacing(1) }}
              >
                {"Query Definition"}
              </Typography>
              <Typography sx={{ maxHeight: "300px", overflowY: "auto", whiteSpace: "pre-wrap" }}>
                {JSON.stringify(editedODM.omniData.query, null, "\t")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item xs={6}>
            {omniODM._id != null && props.onDelete != null && (
              <OmniResponsiveButton
                icon={<DeleteIcon />}
                onClick={() => setShowConfirm(true)}
                buttonText={deleteMessage}
                tooltipTitle="Delete this report"
                disabled={onDelete == null}
              />
            )}
          </Grid>
          <Grid container item justifyContent="flex-end" xs={6} spacing={1}>
            <Grid item>
              {editedODM._id == null && cancelMessage && (
                <Button onClick={onCloseHandler} color="secondary" variant="contained">
                  {cancelMessage}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Button
                onClick={onAcceptHandler}
                color="secondary"
                disabled={!readyOk}
                variant="contained"
              >
                {okMessage}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </DialogActions>
      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete this Report?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default ReportDialog;
